import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Loader2Icon, InfoIcon, DatabaseIcon, AppWindowIcon } from "lucide-react";
import { cn } from "../../../../lib/utils";

interface DatasetBaseTypeProps {
  baseType?: "intent_group" | "application_intent";
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
  ignoredApplicationIds?: number[];
  onChange: (data: Partial<DatasetData>) => void;
}

const DatasetBaseType: React.FC<DatasetBaseTypeProps> = ({
  baseType,
  intentGroupId,
  applicationId,
  intentId,
  ignoredApplicationIds,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loadingIntentGroups, setLoadingIntentGroups] = useState<boolean>(false);
  const [loadingApplications, setLoadingApplications] = useState<boolean>(false);
  const [loadingIntents, setLoadingIntents] = useState<boolean>(false);
  const [intentGroups, setIntentGroups] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [intents, setIntents] = useState<any[]>([]);

  // Fetch intent groups
  useEffect(() => {
    if (baseType === "intent_group" && apiKey) {
      setLoadingIntentGroups(true);
      IntentService.getIntentGroups(
        0,
        100,
        setLoadingIntentGroups,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent groups response:", response);
          if (response && Array.isArray(response)) {
            setIntentGroups(response);
          } else if (response && Array.isArray(response.intent_groups)) {
            setIntentGroups(response.intent_groups);
          } else {
            console.error("Unexpected intent groups response format:", response);
            setIntentGroups([]);
          }
          setLoadingIntentGroups(false);
        },
        (error: any) => {
          console.error("Error fetching intent groups:", error);
          setLoadingIntentGroups(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch applications
  useEffect(() => {
    if (baseType === "application_intent" && apiKey) {
      setLoadingApplications(true);
      ApplicationService.getApplications(
        setLoadingApplications,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Applications response:", response);
          if (response && Array.isArray(response)) {
            setApplications(response);
          } else if (response && Array.isArray(response.items)) {
            setApplications(response.items);
          } else {
            console.error("Unexpected applications response format:", response);
            setApplications([]);
          }
          setLoadingApplications(false);
        },
        (error: any) => {
          console.error("Error fetching applications:", error);
          setLoadingApplications(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch intents for selected application
  useEffect(() => {
    if (baseType === "application_intent" && applicationId && apiKey) {
      setLoadingIntents(true);
      ApplicationService.getApplicationActions(
        applicationId,
        setLoadingIntents,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intents response:", response);
          if (response && Array.isArray(response)) {
            setIntents(response);
          } else if (response && Array.isArray(response.items)) {
            setIntents(response.items);
          } else {
            console.error("Unexpected intents response format:", response);
            setIntents([]);
          }
          setLoadingIntents(false);
        },
        (error: any) => {
          console.error("Error fetching intents:", error);
          setLoadingIntents(false);
        }
      );
    }
  }, [baseType, applicationId, apiKey]);

  // Fetch applications for ignored_application_ids dropdown
  useEffect(() => {
    if (baseType === "intent_group" && apiKey) {
      ApplicationService.getApplications(
        setLoadingApplications,
        apiKey
      ).subscribe(
        (response: any) => {
          if (response && Array.isArray(response)) {
            setApplications(response);
          } else if (response && Array.isArray(response.items)) {
            setApplications(response.items);
          } else {
            console.error("Unexpected applications response format:", response);
            setApplications([]);
          }
        },
        (error: any) => {
          console.error("Error fetching applications:", error);
        }
      );
    }
  }, [baseType, apiKey]);

  const handleBaseTypeChange = (value: "intent_group" | "application_intent") => {
    onChange({
      base_type: value,
      intent_group_id: undefined,
      application_id: undefined,
      intent_id: undefined,
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Choose Base Type
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Select whether to use an Intent Group or an Application/Intent as the base for your dataset
        </p>
      </div>

      {/* Base Type Selection */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Base Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={baseType}
            onValueChange={handleBaseTypeChange}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="intent_group" id="intent_group" />
              <div className="space-y-1">
                <Label htmlFor="intent_group" className="text-sm font-gc-med text-foreground cursor-pointer">
                  Intent Group
                </Label>
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Use Production Data Sampling only. Groups intents across multiple applications.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="application_intent" id="application_intent" />
              <div className="space-y-1">
                <Label htmlFor="application_intent" className="text-sm font-gc-med text-foreground cursor-pointer">
                  Application/Intent
                </Label>
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Use Production Data Sampling or Manual Upload. Specific to one application and intent.
                </p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Intent Group Configuration */}
      {baseType === "intent_group" && (
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InfoIcon className="w-5 h-5 text-maitai-green" />
                Intent Group Selection
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Intent Group</Label>
                  <span className="text-red-500 text-sm">*</span>
                </div>
                <Select
                  value={intentGroupId?.toString()}
                  onValueChange={(value) => onChange({ intent_group_id: parseInt(value) })}
                  disabled={loadingIntentGroups}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={
                      loadingIntentGroups ? "Loading intent groups..." : "Select an intent group"
                    } />
                    {loadingIntentGroups && <Loader2Icon className="w-4 h-4 animate-spin" />}
                  </SelectTrigger>
                  <SelectContent>
                    {intentGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.intent_name || group.name || `Intent Group ${group.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Ignored Applications</Label>
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Select applications to exclude from the dataset
                </p>
                <Select
                  value={ignoredApplicationIds?.join(",")}
                  onValueChange={(value) => {
                    const ids = value ? value.split(",").map(id => parseInt(id)) : [];
                    onChange({ ignored_application_ids: ids });
                  }}
                  disabled={loadingApplications}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={
                      loadingApplications ? "Loading applications..." : "Select applications to ignore"
                    } />
                    {loadingApplications && <Loader2Icon className="w-4 h-4 animate-spin" />}
                  </SelectTrigger>
                  <SelectContent>
                    {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id.toString()}>
                        {app.application_name || `Application ${app.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Application/Intent Configuration */}
      {baseType === "application_intent" && (
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <AppWindowIcon className="w-5 h-5 text-maitai-green" />
                Application & Intent Selection
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Application</Label>
                  <span className="text-red-500 text-sm">*</span>
                </div>
                <Select
                  value={applicationId?.toString()}
                  onValueChange={(value) => {
                    onChange({
                      application_id: parseInt(value),
                      intent_id: undefined,
                    });
                  }}
                  disabled={loadingApplications}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={
                      loadingApplications ? "Loading applications..." : "Select an application"
                    } />
                    {loadingApplications && <Loader2Icon className="w-4 h-4 animate-spin" />}
                  </SelectTrigger>
                  <SelectContent>
                    {applications.map((app) => (
                      <SelectItem key={app.id} value={app.id.toString()}>
                        {app.application_name || `Application ${app.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Intent</Label>
                  <span className="text-red-500 text-sm">*</span>
                </div>
                <Select
                  value={intentId?.toString()}
                  onValueChange={(value) => onChange({ intent_id: parseInt(value) })}
                  disabled={!applicationId || loadingIntents}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={
                      !applicationId
                        ? "Select an application first"
                        : loadingIntents
                        ? "Loading intents..."
                        : "Select an intent"
                    } />
                    {loadingIntents && <Loader2Icon className="w-4 h-4 animate-spin" />}
                  </SelectTrigger>
                  <SelectContent>
                    {intents.map((intent) => (
                      <SelectItem key={intent.id} value={intent.id.toString()}>
                        {intent.action_type || intent.name || `Intent ${intent.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DatasetBaseType;
